import { ArrowLeft, Calendar, Edit2, Plus } from 'lucide-react'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { useAthleteThresholds } from '../../hooks/useAthleteThresholds'
import { getDisciplineColor } from '../../utils/disciplineColors'
import { getEntryIcon } from '../../utils/disciplineIcons'
import { ActionButton } from '../common/ActionButton'
import { Button } from '../common/Button'

const Container = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.lg};
  overflow-y: auto;

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.xxl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  flex: 1;
`

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`

const FilterBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  align-items: center;
`

const FilterButton = styled.button<{ $active: boolean; $color: string }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border: 1px solid ${({ theme, $active, $color }) => ($active ? $color : theme.colors.border)};
  background: ${({ theme, $active, $color }) => ($active ? `${$color}20` : theme.colors.surface)};
  color: ${({ theme, $active, $color }) => ($active ? $color : theme.colors.text)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ $active, $color }) => ($active ? `${$color}30` : `${$color}10`)};
    border-color: ${({ $color }) => $color};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const ThresholdList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`

const ThresholdCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderHover};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`

const ThresholdHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const ThresholdMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  flex: 1;

  @media (max-width: 640px) {
    justify-content: space-between;
  }
`

const ActivityTypeBadge = styled.span<{ $activityType: string; $color: string }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme, $color }) => $color || theme.colors.text};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 500;
  text-transform: capitalize;

  svg {
    width: 16px;
    height: 16px;
  }
`

const ActiveBadge = styled.span`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textInverted};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.xs};
  font-weight: 600;
  text-transform: uppercase;
`

const DateBadge = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.sm};

  @media (max-width: 640px) {
    margin-left: auto;
  }
`

const ThresholdActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  opacity: 0;
  transition: opacity 0.2s ease-in-out;

  ${ThresholdCard}:hover & {
    opacity: 1;
  }
`

const ThresholdValues = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const ValueItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const ValueLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`

const ValueText = styled.span`
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textSecondary};
`

const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.error}20;
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.error};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

interface ThresholdManagementViewProps {
  athleteId: string
  onBack: () => void
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

const formatPace = (pace: number | null): string | null => {
  if (pace === null) {
    return null
  }
  const minutes = Math.floor(pace / 60)
  const seconds = pace % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

type SportFilter = 'all' | 'swimming' | 'cycling' | 'running'

const SPORT_FILTERS: { value: SportFilter; label: string; activityType: string }[] = [
  { value: 'all', label: 'All Sports', activityType: 'other' },
  { value: 'swimming', label: 'Swimming', activityType: 'swimming' },
  { value: 'cycling', label: 'Cycling', activityType: 'cycling' },
  { value: 'running', label: 'Running', activityType: 'running' },
]

export const ThresholdManagementView: React.FC<ThresholdManagementViewProps> = ({ athleteId }) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { thresholds, loading, error } = useAthleteThresholds(athleteId)

  // Initialize filter from URL query parameter
  const sportFromUrl = searchParams.get('sport') as SportFilter
  const initialFilter = SPORT_FILTERS.some((f) => f.value === sportFromUrl) ? sportFromUrl : 'all'
  const [activeFilter, setActiveFilter] = useState<SportFilter>(initialFilter)

  // Update URL when filter changes
  useEffect(() => {
    if (activeFilter === 'all') {
      searchParams.delete('sport')
    } else {
      searchParams.set('sport', activeFilter)
    }
    setSearchParams(searchParams, { replace: true })
  }, [activeFilter, searchParams, setSearchParams])

  const handleEdit = (thresholdId: string) => {
    navigate(`/athletes/${athleteId}/thresholds/${thresholdId}`)
  }

  const handleAddNew = () => {
    navigate(`/athletes/${athleteId}/thresholds/new`)
  }

  const handleBack = () => {
    navigate(`/athletes/${athleteId}`)
  }

  // Sort thresholds by date descending (most recent first)
  const sortedThresholds = [...thresholds].sort((a, b) => {
    return new Date(b.effective_date).getTime() - new Date(a.effective_date).getTime()
  })

  // Find the most recent threshold per sport
  const mostRecentPerSport = new Map<string, string>()
  sortedThresholds.forEach((threshold) => {
    const activityType = threshold.activity_type?.toLowerCase() || 'unknown'
    if (!mostRecentPerSport.has(activityType)) {
      mostRecentPerSport.set(activityType, threshold.id)
    }
  })

  // Filter thresholds based on selected sport
  const filteredThresholds = sortedThresholds.filter((threshold) => {
    if (activeFilter === 'all') return true
    return threshold.activity_type?.toLowerCase() === activeFilter
  })

  // Show older thresholds only when a specific sport is selected
  const displayThresholds =
    activeFilter === 'all'
      ? filteredThresholds.filter((threshold) => {
          const activityType = threshold.activity_type?.toLowerCase() || 'unknown'
          return mostRecentPerSport.get(activityType) === threshold.id
        })
      : filteredThresholds

  return (
    <Container>
      <Header>
        <ActionButton variant="default" onClick={handleBack}>
          <ArrowLeft size={16} />
        </ActionButton>
        <Title>Thresholds</Title>
      </Header>

      <Content>
        {error && <ErrorMessage>{error}</ErrorMessage>}

        {!loading && thresholds.length > 0 && (
          <FilterBar>
            {SPORT_FILTERS.map((filter) => {
              const Icon = getEntryIcon(filter.activityType)
              const color = getDisciplineColor(filter.activityType)
              return (
                <FilterButton
                  key={filter.value}
                  $active={activeFilter === filter.value}
                  $color={color}
                  onClick={() => setActiveFilter(filter.value)}
                >
                  <Icon />
                  {filter.label}
                </FilterButton>
              )
            })}
            <Button variant="primary" onClick={handleAddNew} style={{ marginLeft: 'auto' }}>
              <Plus size={16} />
              Add Threshold
            </Button>
          </FilterBar>
        )}

        {!loading && thresholds.length === 0 && (
          <FilterBar>
            <Button variant="primary" onClick={handleAddNew}>
              <Plus size={16} />
              Add Threshold
            </Button>
          </FilterBar>
        )}

        <Section>
          {loading ? (
            <EmptyState>Loading thresholds...</EmptyState>
          ) : thresholds.length === 0 ? (
            <EmptyState>No threshold data yet. Add your first threshold entry above.</EmptyState>
          ) : displayThresholds.length === 0 ? (
            <EmptyState>No thresholds found for the selected sport.</EmptyState>
          ) : (
            <ThresholdList>
              {displayThresholds.map((threshold) => {
                const Icon = getEntryIcon(threshold.activity_type)
                const activityType = threshold.activity_type?.toLowerCase() || 'unknown'
                const isActive = mostRecentPerSport.get(activityType) === threshold.id
                return (
                  <ThresholdCard key={threshold.id}>
                    <ThresholdHeader>
                      <ThresholdMeta>
                        <ActivityTypeBadge
                          $activityType={threshold.activity_type || 'unknown'}
                          $color={getDisciplineColor(threshold.activity_type || 'unknown')}
                        >
                          <Icon />
                          {threshold.activity_type || 'unknown'}
                        </ActivityTypeBadge>
                        <DateBadge>
                          <Calendar size={14} />
                          {formatDate(threshold.effective_date)}
                        </DateBadge>
                        {isActive && <ActiveBadge>Current</ActiveBadge>}
                      </ThresholdMeta>
                      <ThresholdActions>
                        <Button variant="secondary" size="sm" onClick={() => handleEdit(threshold.id)}>
                          <Edit2 size={14} />
                          Edit
                        </Button>
                      </ThresholdActions>
                    </ThresholdHeader>

                    <ThresholdValues>
                      {threshold.vt1_bpm && (
                        <ValueItem>
                          <ValueLabel>VT1</ValueLabel>
                          <ValueText>{threshold.vt1_bpm} bpm</ValueText>
                          {threshold.vt1_pace_min_per_kmh && (
                            <ValueText>{formatPace(threshold.vt1_pace_min_per_kmh)} min/km</ValueText>
                          )}
                          {threshold.vt1_power_watts && <ValueText>{threshold.vt1_power_watts} W</ValueText>}
                        </ValueItem>
                      )}
                      {threshold.vt2_bpm && (
                        <ValueItem>
                          <ValueLabel>VT2</ValueLabel>
                          <ValueText>{threshold.vt2_bpm} bpm</ValueText>
                          {threshold.vt2_pace_min_per_kmh && (
                            <ValueText>{formatPace(threshold.vt2_pace_min_per_kmh)} min/km</ValueText>
                          )}
                          {threshold.vt2_power_watts && <ValueText>{threshold.vt2_power_watts} W</ValueText>}
                        </ValueItem>
                      )}
                      {threshold.max_hr_bpm && (
                        <ValueItem>
                          <ValueLabel>Max HR</ValueLabel>
                          <ValueText>{threshold.max_hr_bpm} bpm</ValueText>
                        </ValueItem>
                      )}
                    </ThresholdValues>
                  </ThresholdCard>
                )
              })}
            </ThresholdList>
          )}
        </Section>
      </Content>
    </Container>
  )
}
